import type { AnyAction } from 'redux';
import { Card, Form, FormButtonRow, FormGroup, FormParagraph, IconButton } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { ValidationErrors } from '../../../../utils/validation';
import type { IServiceConsumer } from '../../../interfaces/serviceConsumer';
import { serviceConsumerGgzPreferencesSchema } from '../../../../../common/validation/serviceConsumer';
import { fetchJson } from '../../../../store/fetch';
import { fetchServiceConsumers } from '../../../../store/serviceConsumers/serviceConsumerActions';
import { translateKey } from '../../../../translate';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../../utils/validation';
import { InputTriState } from '../../../elements/common';
import { FormContextProvider } from '../../../elements/formContext';
import { InputContainer } from '../../../elements/forms/InputContainer';
import { NumberInput } from '../../../fields/Number';
import { CancelButton } from '../../../userDataManagement/CancelButton';
import { SaveButton } from '../../../userDataManagement/SaveButton';

interface IGgzExaminationPersonalSettingsProps {
    isReadOnly: boolean;
    serviceConsumerId: string;
}

export function GgzExaminationPersonalSettings(props: IGgzExaminationPersonalSettingsProps) {
    const dispatch = useDispatch();

    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [originalServiceConsumer, setOriginalServiceConsumer] = useState<IServiceConsumer>();
    const [serviceConsumer, setServiceConsumer] = useState<IServiceConsumer>();

    useEffect(() => {
        loadServiceConsumer(props.serviceConsumerId);
    }, [props.serviceConsumerId]);

    if (!serviceConsumer) {
        return null;
    }

    return (
        <FormContextProvider
            value={{
                placeholderGroupName: 'GgzoPersonalSettingsPreferences',
                item: serviceConsumer,
                errors: errors,
                onChange: async (key, value) => handleChange(key, value),
                onDoValidate: async (key) => validateOne(key),
            }}
        >
            <Card>
                <Card.Transition type={isEditing ? 'edit' : 'read'} />
                <Card.Header
                    action={
                        !props.isReadOnly && !isEditing ? (
                            <IconButton
                                icon="edit-light"
                                labelText="edit"
                                onClick={() => setIsEditing(true)}
                            />
                        ) : undefined
                    }
                    title={translateKey('personalSettings.followUpMeasurementGgz')}
                />
                <Card.Body>
                    {!isEditing && (
                        <>
                            <FormGroup label={translateKey('serviceConsumer.sendFollowUpMeasurementGgz')}>
                                <FormParagraph>
                                    {translateKey(`common.${serviceConsumer.followUpMeasurementGgz ? 'yes' : 'no'}`)}
                                </FormParagraph>
                            </FormGroup>

                            {serviceConsumer.followUpMeasurementGgz &&
                                serviceConsumer.weeksForFollowUpMeasurementGgz && (
                                    <FormGroup label="Sturen na ">
                                        <FormParagraph>{`${serviceConsumer.weeksForFollowUpMeasurementGgz} weken`}</FormParagraph>
                                    </FormGroup>
                                )}
                        </>
                    )}

                    {isEditing && (
                        <Form submitOnEnter={false}>
                            <FormGroup label={translateKey('serviceConsumer.sendFollowUpMeasurementGgz')}>
                                <InputTriState
                                    error={errors.get('followUpMeasurementGgz')}
                                    fieldKey="followUpMeasurementGgz"
                                    name="followUpMeasurementGgz"
                                    notNullable
                                    onChange={(value) => {
                                        const update: Partial<IServiceConsumer> = { followUpMeasurementGgz: !!value };
                                        if (!value) {
                                            update.weeksForFollowUpMeasurementGgz = null;
                                        }
                                        setServiceConsumer({ ...serviceConsumer, ...update } as IServiceConsumer);
                                    }}
                                />
                            </FormGroup>

                            {serviceConsumer.followUpMeasurementGgz && (
                                <FormGroup label="Sturen na">
                                    <InputContainer className="col-sm-12 col-xl-6">
                                        <NumberInput
                                            disabled={!serviceConsumer.followUpMeasurementGgz}
                                            error={errors.get('weeksForFollowUpMeasurementGgz')}
                                            fieldKey="weeksForFollowUpMeasurementGgz"
                                            placeholder="weken"
                                            text=" weken"
                                            width="medium"
                                        />
                                    </InputContainer>
                                </FormGroup>
                            )}

                            <FormButtonRow>
                                <CancelButton
                                    disabled={isSaving}
                                    onCancel={handleCancel}
                                />
                                <SaveButton
                                    disabled={isSaving}
                                    onSave={handleSubmit}
                                />
                            </FormButtonRow>
                        </Form>
                    )}
                </Card.Body>
            </Card>
        </FormContextProvider>
    );

    async function loadServiceConsumer(id: string) {
        const url = `/api/serviceConsumers/${id}`;
        const result = await fetchJson(url, 'GET');
        setOriginalServiceConsumer(result.json);
        setServiceConsumer(result.json);
    }

    function handleChange<K extends keyof IServiceConsumer>(name: K, value: IServiceConsumer[K]) {
        setServiceConsumer({ ...serviceConsumer, [name]: value } as IServiceConsumer);
    }

    function validateOne(name: keyof IServiceConsumer) {
        setErrors(updateValidationErrorsForOne(serviceConsumerGgzPreferencesSchema, serviceConsumer, errors, name));
    }

    async function handleCancel() {
        setServiceConsumer(originalServiceConsumer);
        setErrors(emptyValidationErrors);
        setIsEditing(false);
    }

    async function handleSubmit() {
        if (serviceConsumer) {
            setIsSaving(true);

            const { errors: validationErrors, item } = validate(serviceConsumerGgzPreferencesSchema, serviceConsumer);

            if (validationErrors.size > 0) {
                setErrors(validationErrors);
            } else {
                const url = `/api/serviceConsumers/${props.serviceConsumerId}/ggzExaminationPersonalSettings`;
                await fetchJson(url, 'POST', item);
                // Update serviceConsumers in Redux state
                dispatch(
                    fetchServiceConsumers({
                        employmentId: serviceConsumer.employment.id,
                        isActive: true,
                    }) as unknown as AnyAction
                );

                setOriginalServiceConsumer(serviceConsumer);
                setErrors(emptyValidationErrors);
                setIsEditing(false);
            }

            setIsSaving(false);
        }
    }
}
